import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { createPortal } from 'react-dom';
import { ArrowLeft, FileText, Download, Calendar, LayoutTemplate, Save, Check, ChevronDown, ChevronUp, Settings2, AlignLeft, Eye, X, BarChart3 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Checkbox } from '../components/ui/checkbox';
import { Separator } from '../components/ui/separator';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { motion, AnimatePresence } from 'framer-motion';
import { clients, sentimentOverTime, coverageByTier, topSources, spokespersonMentions } from '../lib/data';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, ResponsiveContainer, Tooltip } from 'recharts';
const TEMPLATES = [{
  id: 'executive',
  name: 'Executive Summary',
  description: 'Sintesi rapida per il top management. Solo i dati essenziali.',
  sectionsCount: 3,
  sections: {
    summary: true,
    kpi: true,
    topArticles: true,
    sentiment: false,
    tier: false,
    sources: false,
    spokespersons: false,
    allArticles: false
  },
  preview: <div className="flex flex-col gap-1.5 p-2 w-full h-full bg-muted/20 rounded-md border border-border/50" data-id="element-1404">
        <div className="h-2 w-1/3 bg-accent/40 rounded-sm mb-1" data-id="element-1405" />
        <div className="h-6 w-full bg-accent/20 rounded-sm" data-id="element-1406" />
        <div className="grid grid-cols-2 gap-1.5" data-id="element-1407">
          <div className="h-8 bg-muted rounded-sm" data-id="element-1408" />
          <div className="h-8 bg-muted rounded-sm" data-id="element-1409" />
        </div>
        <div className="h-10 w-full bg-muted rounded-sm mt-auto" data-id="element-1410" />
      </div>
}, {
  id: 'standard',
  name: 'Report Standard',
  description: 'Il formato più utilizzato. Include analisi qualitativa e quantitativa.',
  sectionsCount: 5,
  sections: {
    summary: true,
    kpi: true,
    topArticles: true,
    sentiment: true,
    tier: true,
    sources: false,
    spokespersons: false,
    allArticles: false
  },
  preview: <div className="flex flex-col gap-1.5 p-2 w-full h-full bg-muted/20 rounded-md border border-border/50" data-id="element-1411">
        <div className="h-2 w-1/3 bg-accent/40 rounded-sm mb-1" data-id="element-1412" />
        <div className="h-6 w-full bg-accent/20 rounded-sm" data-id="element-1413" />
        <div className="grid grid-cols-2 gap-1.5" data-id="element-1414">
          <div className="h-8 bg-muted rounded-sm" data-id="element-1415" />
          <div className="h-8 bg-muted rounded-sm" data-id="element-1416" />
        </div>
        <div className="grid grid-cols-2 gap-1.5 mt-auto" data-id="element-1417">
          <div className="h-6 bg-accent/10 rounded-sm" data-id="element-1418" />
          <div className="h-6 bg-accent/10 rounded-sm" data-id="element-1419" />
        </div>
      </div>
}, {
  id: 'full',
  name: 'Analisi Completa',
  description: 'Tutti i dati disponibili, inclusa la lista completa degli articoli.',
  sectionsCount: 8,
  sections: {
    summary: true,
    kpi: true,
    topArticles: true,
    sentiment: true,
    tier: true,
    sources: true,
    spokespersons: true,
    allArticles: true
  },
  preview: <div className="flex flex-col gap-1.5 p-2 w-full h-full bg-muted/20 rounded-md border border-border/50" data-id="element-1420">
        <div className="h-2 w-1/3 bg-accent/40 rounded-sm mb-1" data-id="element-1421" />
        <div className="h-4 w-full bg-accent/20 rounded-sm" data-id="element-1422" />
        <div className="grid grid-cols-3 gap-1" data-id="element-1423">
          <div className="h-6 bg-muted rounded-sm" data-id="element-1424" />
        </div>
        <div className="grid grid-cols-2 gap-1.5" data-id="element-1425">
          <div className="h-6 bg-accent/10 rounded-sm" data-id="element-1426" />
          <div className="h-6 bg-accent/10 rounded-sm" data-id="element-1427" />
        </div>
        <div className="h-8 w-full bg-muted/50 rounded-sm mt-auto" data-id="element-1428" />
      </div>
}];
const AVAILABLE_SECTIONS = [{
  id: 'summary',
  label: 'Riepilogo AI (Executive Summary)'
}, {
  id: 'kpi',
  label: 'KPI Principali'
}, {
  id: 'topArticles',
  label: 'Top Articoli'
}, {
  id: 'sentiment',
  label: 'Grafico Sentiment'
}, {
  id: 'tier',
  label: 'Copertura per Tier'
}, {
  id: 'sources',
  label: 'Top Testate'
}, {
  id: 'spokespersons',
  label: 'Portavoce citati'
}, {
  id: 'allArticles',
  label: 'Lista completa articoli'
}];
export function ReportBuilder() {
  const navigate = useNavigate();
  const [reportName, setReportName] = useState('Report Mensile Aprile 2026');
  const [template, setTemplate] = useState('standard');
  const [sections, setSections] = useState(TEMPLATES[1].sections);
  const [customizeOpen, setCustomizeOpen] = useState(false);
  const [notesOpen, setNotesOpen] = useState(false);
  const [notes, setNotes] = useState('');
  const [previewOpen, setPreviewOpen] = useState(false);
  const [startDate, setStartDate] = useState('2025-04-01');
  const [endDate, setEndDate] = useState('2025-04-30');
  const intrumClient = clients.find(c => c.id === 'intrum') || clients[0];
  useEffect(() => {
    if (previewOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [previewOpen]);
  const handleTemplateChange = (templateId: string) => {
    setTemplate(templateId);
    const selectedTemplate = TEMPLATES.find(t => t.id === templateId);
    if (selectedTemplate) {
      setSections(selectedTemplate.sections);
    }
  };
  const toggleSection = (key: keyof typeof sections) => {
    setSections(prev => {
      const newSections = {
        ...prev,
        [key]: !prev[key]
      };
      // Check if it matches any template exactly
      const matchedTemplate = TEMPLATES.find(t => Object.entries(t.sections).every(([k, v]) => newSections[k as keyof typeof sections] === v));
      if (matchedTemplate) {
        setTemplate(matchedTemplate.id);
      } else {
        setTemplate('custom');
      }
      return newSections;
    });
  };
  const selectedCount = Object.values(sections).filter(Boolean).length;
  return <div className="h-full flex flex-col bg-background relative" data-id="element-1429">
      {/* Header */}
      <div className="bg-card border-b border-border" data-id="element-1430">
        <div className="px-6 pt-6 md:pt-8 max-w-[1400px] mx-auto w-full" data-id="element-1431">
          <button onClick={() => navigate('/reports')} className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-4 transition-colors" data-id="element-1432">
            <ArrowLeft className="h-3.5 w-3.5" data-id="element-1433" />
            Report
          </button>

          <div className="flex flex-col md:flex-row md:items-start justify-between gap-4 mb-6" data-id="element-1434">
            <div className="min-w-0 flex-1" data-id="element-1435">
              <input type="text" value={reportName} onChange={e => setReportName(e.target.value)} className="text-2xl md:text-3xl font-normal tracking-tight leading-tight mb-2 bg-transparent border-none focus:outline-none focus:ring-0 p-0 m-0 w-full truncate hover:bg-muted/30 px-2 -ml-2 rounded transition-colors cursor-text" data-id="element-1436" />
              <div className="text-sm text-muted-foreground font-normal px-2 -ml-2" data-id="element-1437">
                Configurazione nuovo report
              </div>
            </div>

            <div className="flex items-center gap-2.5 flex-shrink-0" data-id="element-1438">
              <div className="flex items-center gap-2 text-sm bg-muted/30 px-3 py-2 rounded-lg border border-border" data-id="element-1439">
                <Calendar className="h-4 w-4 text-muted-foreground flex-shrink-0" data-id="element-1440" />
                <label className="text-muted-foreground text-xs font-medium whitespace-nowrap" data-id="element-1441">
                  Da
                </label>
                <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} className="bg-transparent border-none outline-none text-sm text-foreground font-medium w-[130px] cursor-pointer" data-id="element-1442" />
                <span className="text-muted-foreground mx-1" data-id="element-1443">—</span>
                <label className="text-muted-foreground text-xs font-medium whitespace-nowrap" data-id="element-1444">
                  A
                </label>
                <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} className="bg-transparent border-none outline-none text-sm text-foreground font-medium w-[130px] cursor-pointer" data-id="element-1445" />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 overflow-auto pb-24" data-id="element-1446">
        <div className="max-w-[1400px] mx-auto px-6 py-6 w-full space-y-10" data-id="element-1447">
          {/* Templates Hero */}
          <section data-id="element-1448">
            <div className="mb-6" data-id="element-1449">
              <h2 className="text-lg font-normal flex items-center gap-2" data-id="element-1450">
                <LayoutTemplate className="h-5 w-5 text-accent" data-id="element-1451" />
                Scegli un template
              </h2>
              <p className="text-sm text-muted-foreground mt-1" data-id="element-1452">
                Seleziona un formato di partenza. Potrai personalizzare le
                sezioni in seguito.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-1453">
              {TEMPLATES.map(t => {
              const isSelected = template === t.id;
              return <motion.div key={t.id} whileHover={{
                scale: 1.02
              }} whileTap={{
                scale: 0.98
              }} onClick={() => handleTemplateChange(t.id)} className={`relative cursor-pointer rounded-xl border p-5 transition-all duration-200 flex flex-col h-full bg-card ${isSelected ? 'border-accent shadow-md ring-1 ring-accent/20' : 'border-border hover:border-accent/50 hover:shadow-sm'}`} data-id="element-1454">
                    {isSelected && <div className="absolute -top-3 -right-3 w-7 h-7 bg-accent text-white rounded-full flex items-center justify-center shadow-sm z-10" data-id="element-1455">
                        <Check className="h-4 w-4" data-id="element-1456" />
                      </div>}

                    <div className="h-32 mb-5 rounded-lg overflow-hidden flex-shrink-0" data-id="element-1457">
                      {t.preview}
                    </div>

                    <div className="mt-auto" data-id="element-1458">
                      <div className="flex items-center justify-between mb-2" data-id="element-1459">
                        <h3 className="font-normal text-base" data-id="element-1460">{t.name}</h3>
                        <span className="text-xs font-medium bg-muted px-2 py-1 rounded-md text-muted-foreground" data-id="element-1461">
                          {t.sectionsCount} sezioni
                        </span>
                      </div>
                      <p className="text-sm text-muted-foreground leading-relaxed" data-id="element-1462">
                        {t.description}
                      </p>
                    </div>
                  </motion.div>;
            })}
            </div>
          </section>

          <Separator data-id="element-1463" />

          {/* Customize Sections */}
          <section data-id="element-1464">
            <button onClick={() => setCustomizeOpen(!customizeOpen)} className="w-full flex items-center justify-between py-2 group" data-id="element-1465">
              <div className="flex items-center gap-2" data-id="element-1466">
                <Settings2 className="h-5 w-5 text-muted-foreground group-hover:text-foreground transition-colors" data-id="element-1467" />
                <h2 className="text-lg font-normal group-hover:text-accent transition-colors" data-id="element-1468">
                  Personalizza sezioni
                </h2>
              </div>
              {customizeOpen ? <ChevronUp className="h-5 w-5 text-muted-foreground" data-id="element-1469" /> : <ChevronDown className="h-5 w-5 text-muted-foreground" data-id="element-1470" />}
            </button>

            <AnimatePresence data-id="element-1471">
              {customizeOpen && <motion.div initial={{
              height: 0,
              opacity: 0
            }} animate={{
              height: 'auto',
              opacity: 1
            }} exit={{
              height: 0,
              opacity: 0
            }} transition={{
              duration: 0.3,
              ease: 'easeInOut'
            }} className="overflow-hidden" data-id="element-1472">
                  <div className="pt-6 pb-2 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-1473">
                    {AVAILABLE_SECTIONS.map(section => {
                  const isChecked = sections[section.id as keyof typeof sections];
                  return <div key={section.id} onClick={() => toggleSection(section.id as keyof typeof sections)} className={`flex items-start gap-3 p-4 rounded-lg border cursor-pointer transition-colors ${isChecked ? 'border-accent/50 bg-accent/5' : 'border-border bg-card hover:border-accent/30'}`} data-id="element-1474">
                          <Checkbox checked={isChecked} onCheckedChange={() => toggleSection(section.id as keyof typeof sections)} className="mt-0.5" data-id="element-1475" />
                          <span className="text-sm font-medium leading-snug select-none" data-id="element-1476">
                            {section.label}
                          </span>
                        </div>;
                })}
                  </div>
                </motion.div>}
            </AnimatePresence>
          </section>

          <Separator data-id="element-1477" />

          {/* Notes Section */}
          <section data-id="element-1478">
            <button onClick={() => setNotesOpen(!notesOpen)} className="w-full flex items-center justify-between py-2 group" data-id="element-1479">
              <div className="flex items-center gap-2" data-id="element-1480">
                <AlignLeft className="h-5 w-5 text-muted-foreground group-hover:text-foreground transition-colors" data-id="element-1481" />
                <h2 className="text-lg font-normal group-hover:text-accent transition-colors" data-id="element-1482">
                  Note aggiuntive
                </h2>
              </div>
              {notesOpen ? <ChevronUp className="h-5 w-5 text-muted-foreground" data-id="element-1483" /> : <ChevronDown className="h-5 w-5 text-muted-foreground" data-id="element-1484" />}
            </button>

            <AnimatePresence data-id="element-1485">
              {notesOpen && <motion.div initial={{
              height: 0,
              opacity: 0
            }} animate={{
              height: 'auto',
              opacity: 1
            }} exit={{
              height: 0,
              opacity: 0
            }} transition={{
              duration: 0.3,
              ease: 'easeInOut'
            }} className="overflow-hidden" data-id="element-1486">
                  <div className="pt-6 pb-2" data-id="element-1487">
                    <textarea value={notes} onChange={e => setNotes(e.target.value)} placeholder="Inserisci un commento o una nota introduttiva per il cliente che apparirà all'inizio del report..." className="w-full px-4 py-3 text-sm border border-input rounded-lg bg-card focus:outline-none focus:ring-2 focus:ring-accent min-h-[120px] resize-y shadow-sm" data-id="element-1488" />
                  </div>
                </motion.div>}
            </AnimatePresence>
          </section>
        </div>
      </div>

      {/* Sticky Bottom Bar */}
      <div className="sticky bottom-0 left-0 right-0 border-t border-border bg-card/95 backdrop-blur supports-[backdrop-filter]:bg-card/80 p-4 md:px-8 z-20 shadow-[0_-4px_20px_-10px_rgba(0,0,0,0.1)]" data-id="element-1489">
        <div className="max-w-[1400px] mx-auto flex flex-col sm:flex-row items-center justify-between gap-4" data-id="element-1490">
          <div className="text-sm font-medium text-muted-foreground" data-id="element-1491">
            <span className="text-foreground font-semibold" data-id="element-1492">
              {selectedCount}
            </span>{' '}
            sezioni selezionate
          </div>

          <div className="flex items-center gap-3 w-full sm:w-auto" data-id="element-1493">
            <Button variant="outline" className="rounded-full flex-1 sm:flex-none" data-id="element-1494">
              <Save className="h-4 w-4 mr-2" data-id="element-1495" />
              Salva Bozza
            </Button>
            <Button variant="outline" className="rounded-full flex-1 sm:flex-none" data-id="element-1496">
              <Download className="h-4 w-4 mr-2" data-id="element-1497" />
              Esporta PDF
            </Button>
            <Button variant="outline" className="rounded-full flex-1 sm:flex-none" onClick={() => setPreviewOpen(true)} data-id="element-1498">
              <Eye className="h-4 w-4 mr-2" data-id="element-1499" />
              Anteprima
            </Button>
            <Button className="rounded-full flex-1 sm:flex-none px-8" data-id="element-1500">
              Genera Report
            </Button>
          </div>
        </div>
      </div>

      {/* Preview Overlay */}
      <AnimatePresence data-id="element-1501">
        {previewOpen && <motion.div key="preview-overlay" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.3,
        ease: 'easeInOut'
      }} className="fixed inset-0 bg-white flex flex-col" style={{
        zIndex: 99999
      }} data-id="element-1502">
            {/* Top bar - matching AI Mode style */}
            <div className="h-16 border-b border-border flex items-center justify-between px-6 flex-shrink-0 bg-white" data-id="element-1503">
              <button type="button" onClick={e => {
            e.preventDefault();
            e.stopPropagation();
            setPreviewOpen(false);
          }} className="w-10 h-10 rounded-full flex items-center justify-center cursor-pointer hover:bg-muted transition-colors bg-transparent border-none outline-none" style={{
            zIndex: 100000
          }} aria-label="Chiudi anteprima" data-id="element-1504">
                <X className="h-5 w-5 text-foreground pointer-events-none" data-id="element-1505" />
              </button>

              <div className="flex items-center gap-2" data-id="element-1506">
                <Eye className="h-4 w-4 text-accent" data-id="element-1507" />
                <span className="font-medium text-sm text-foreground" data-id="element-1508">
                  Anteprima Report
                </span>
              </div>

              <div className="flex items-center gap-3" data-id="element-1509">
                <Button variant="outline" className="rounded-full h-9 px-4 text-sm border-accent text-accent hover:bg-accent/10" data-id="element-1510">
                  <Download className="h-4 w-4 mr-2" data-id="element-1511" />
                  Esporta PDF
                </Button>
                <Button className="rounded-full h-9 px-4 text-sm" data-id="element-1512">
                  Genera Report
                </Button>
              </div>
            </div>

            {/* Document Area */}
            <div className="flex-1 overflow-auto bg-[#F0F1F3]" data-id="element-1513">
              <div className="py-10 px-6" data-id="element-1514">
                <div className="w-[794px] min-h-[1123px] bg-white mx-auto shadow-[0_2px_20px_rgba(0,0,0,0.08)] flex flex-col p-16 text-foreground rounded-sm" data-id="element-1515">
                  {/* Document Header */}
                  <div className="flex justify-between items-start mb-14" data-id="element-1516">
                    <div data-id="element-1517">
                      <div className="w-12 h-1 bg-accent mb-6 rounded-full" data-id="element-1518" />
                      <h1 className="text-[28px] font-bold mb-3 text-[#1A1F2E] leading-tight" data-id="element-1519">
                        {reportName}
                      </h1>
                      <p className="text-sm text-muted-foreground" data-id="element-1520">
                        Generato il{' '}
                        {new Date().toLocaleDateString('it-IT', {
                      day: 'numeric',
                      month: 'long',
                      year: 'numeric'
                    })}
                      </p>
                      <p className="text-sm text-muted-foreground mt-1" data-id="element-1521">
                        UCEI — Media Intelligence
                      </p>
                    </div>
                    <img src={intrumClient.logo} alt="Logo" className="h-8 object-contain mt-2" style={{
                  filter: 'brightness(0)'
                }} data-id="element-1522" />
                  </div>

                  {/* Notes */}
                  {notes && <div className="mb-10 text-[15px] leading-relaxed text-muted-foreground italic border-l-4 border-accent pl-5 py-1" data-id="element-1523">
                      {notes}
                    </div>}

                  {/* Sections */}
                  <div className="space-y-12 flex-1" data-id="element-1524">
                    {sections.summary && <section data-id="element-1525">
                        <h2 className="text-lg font-semibold mb-4 text-[#1A1F2E] flex items-center gap-2" data-id="element-1526">
                          <div className="w-1.5 h-1.5 rounded-full bg-accent" data-id="element-1527" />
                          Executive Summary
                        </h2>
                        <div className="text-[15px] leading-relaxed text-muted-foreground space-y-3 pl-4" data-id="element-1528">
                          <p data-id="element-1529">
                            Nel periodo analizzato, Intrum ha registrato una
                            copertura mediatica significativa con 20 articoli
                            pubblicati su 12 testate nazionali e specializzate.
                            Il sentiment complessivo è prevalentemente positivo
                            (65%), con un AVE totale di €187k.
                          </p>
                          <p data-id="element-1530">
                            I temi principali riguardano le operazioni di
                            gestione dei crediti deteriorati (NPL), le strategie
                            di espansione nel mercato italiano e il
                            posizionamento competitivo nel settore del credit
                            management.
                          </p>
                        </div>
                      </section>}

                    {sections.kpi && <section data-id="element-1531">
                        <h2 className="text-lg font-semibold mb-4 text-[#1A1F2E] flex items-center gap-2" data-id="element-1532">
                          <div className="w-1.5 h-1.5 rounded-full bg-accent" data-id="element-1533" />
                          KPI Principali
                        </h2>
                        <div className="grid grid-cols-2 gap-4 pl-4" data-id="element-1534">
                          <div className="p-5 border border-border rounded-lg" data-id="element-1535">
                            <div className="text-xs uppercase tracking-wider text-muted-foreground mb-2" data-id="element-1536">
                              Articoli totali
                            </div>
                            <div className="text-3xl font-bold text-[#1A1F2E]" style={{
                        fontVariantNumeric: 'tabular-nums'
                      }} data-id="element-1537">
                              20
                            </div>
                            <div className="text-xs text-green-600 mt-1" data-id="element-1538">
                              +25% vs periodo prec.
                            </div>
                          </div>
                          <div className="p-5 border border-border rounded-lg" data-id="element-1539">
                            <div className="text-xs uppercase tracking-wider text-muted-foreground mb-2" data-id="element-1540">
                              Score medio
                            </div>
                            <div className="text-3xl font-bold text-accent" style={{
                        fontVariantNumeric: 'tabular-nums'
                      }} data-id="element-1541">
                              7.4
                            </div>
                            <div className="text-xs text-green-600 mt-1" data-id="element-1542">
                              +8.8% vs periodo prec.
                            </div>
                          </div>
                          <div className="p-5 border border-border rounded-lg" data-id="element-1543">
                            <div className="text-xs uppercase tracking-wider text-muted-foreground mb-2" data-id="element-1544">
                              AVE totale
                            </div>
                            <div className="text-3xl font-bold text-[#1A1F2E]" style={{
                        fontVariantNumeric: 'tabular-nums'
                      }} data-id="element-1545">
                              €187k
                            </div>
                            <div className="text-xs text-green-600 mt-1" data-id="element-1546">
                              +31.7% vs periodo prec.
                            </div>
                          </div>
                          <div className="p-5 border border-border rounded-lg" data-id="element-1547">
                            <div className="text-xs uppercase tracking-wider text-muted-foreground mb-2" data-id="element-1548">
                              Sentiment
                            </div>
                            <div className="flex items-baseline gap-2" data-id="element-1549">
                              <span className="text-3xl font-bold text-accent" style={{
                          fontVariantNumeric: 'tabular-nums'
                        }} data-id="element-1550">
                                65%
                              </span>
                              <span className="text-sm text-muted-foreground" data-id="element-1551">
                                positivo
                              </span>
                            </div>
                            <div className="flex gap-1 mt-2" data-id="element-1552">
                              <div className="h-1.5 rounded-full bg-accent" style={{
                          width: '65%'
                        }} data-id="element-1553" />
                              <div className="h-1.5 rounded-full bg-[#64B5F6]" style={{
                          width: '25%'
                        }} data-id="element-1554" />
                              <div className="h-1.5 rounded-full bg-[#1A3A5C]" style={{
                          width: '10%'
                        }} data-id="element-1555" />
                            </div>
                          </div>
                        </div>
                      </section>}

                    {sections.topArticles && <section data-id="element-1556">
                        <h2 className="text-lg font-semibold mb-4 text-[#1A1F2E] flex items-center gap-2" data-id="element-1557">
                          <div className="w-1.5 h-1.5 rounded-full bg-accent" data-id="element-1558" />
                          Top Articoli
                        </h2>
                        <div className="space-y-0 pl-4" data-id="element-1559">
                          {[{
                      title: 'Intrum acquisisce portafoglio NPL da €1.2 miliardi da UniCredit',
                      source: 'Il Sole 24 Ore',
                      score: 9.2,
                      date: '15 Apr'
                    }, {
                      title: 'Credit management: Intrum rafforza la presenza in Italia',
                      source: 'MF Milano Finanza',
                      score: 8.7,
                      date: '12 Apr'
                    }, {
                      title: 'Il mercato NPL italiano attira gli investitori nordeuropei',
                      source: 'Corriere della Sera',
                      score: 8.1,
                      date: '10 Apr'
                    }].map((article, i) => <div key={i} className="flex items-center justify-between py-3 border-b border-border last:border-0" data-id="element-1560">
                              <div className="flex-1 min-w-0 pr-4" data-id="element-1561">
                                <div className="text-[14px] font-medium text-[#1A1F2E] leading-snug" data-id="element-1562">
                                  {article.title}
                                </div>
                                <div className="text-xs text-muted-foreground mt-1" data-id="element-1563">
                                  {article.source} · {article.date}
                                </div>
                              </div>
                              <div className="text-sm font-semibold text-accent flex-shrink-0" style={{
                        fontVariantNumeric: 'tabular-nums'
                      }} data-id="element-1564">
                                {article.score}
                              </div>
                            </div>)}
                        </div>
                      </section>}

                    {sections.sentiment && <section data-id="element-1565">
                        <h2 className="text-lg font-semibold mb-4 text-[#1A1F2E] flex items-center gap-2" data-id="element-1566">
                          <div className="w-1.5 h-1.5 rounded-full bg-accent" data-id="element-1567" />
                          Andamento Sentiment
                        </h2>
                        <div className="ml-4" data-id="element-1568">
                          <AreaChart width={620} height={180} data={sentimentOverTime} margin={{
                      top: 5,
                      right: 0,
                      bottom: 0,
                      left: -20
                    }} data-id="element-1569">
                            <CartesianGrid strokeDasharray="3 3" stroke="#E8E8E5" vertical={false} data-id="element-1570" />
                            <XAxis dataKey="date" tick={{
                        fontSize: 10
                      }} stroke="#8B8680" axisLine={false} tickLine={false} dy={5} data-id="element-1571" />
                            <YAxis tick={{
                        fontSize: 10
                      }} stroke="#8B8680" axisLine={false} tickLine={false} data-id="element-1572" />
                            <Tooltip data-id="element-1573" />
                            <Area type="monotone" dataKey="positive" stackId="1" stroke="#0187c3" fill="#0187c3" fillOpacity={0.5} strokeWidth={1.5} data-id="element-1574" />
                            <Area type="monotone" dataKey="neutral" stackId="1" stroke="#64B5F6" fill="#64B5F6" fillOpacity={0.35} strokeWidth={1.5} data-id="element-1575" />
                            <Area type="monotone" dataKey="negative" stackId="1" stroke="#1A3A5C" fill="#1A3A5C" fillOpacity={0.5} strokeWidth={1.5} data-id="element-1576" />
                          </AreaChart>
                        </div>
                      </section>}

                    {sections.tier && <section data-id="element-1577">
                        <h2 className="text-lg font-semibold mb-4 text-[#1A1F2E] flex items-center gap-2" data-id="element-1578">
                          <div className="w-1.5 h-1.5 rounded-full bg-accent" data-id="element-1579" />
                          Copertura per Tier
                        </h2>
                        <div className="space-y-3 pl-4" data-id="element-1580">
                          {[{
                      tier: 'Tier 1',
                      count: 5,
                      pct: 25
                    }, {
                      tier: 'Tier 2',
                      count: 8,
                      pct: 40
                    }, {
                      tier: 'Tier 3',
                      count: 4,
                      pct: 20
                    }, {
                      tier: 'Specializzate',
                      count: 3,
                      pct: 15
                    }].map(item => <div key={item.tier} data-id="element-1581">
                              <div className="flex items-center justify-between mb-1.5 text-sm" data-id="element-1582">
                                <span className="font-medium text-[#1A1F2E]" data-id="element-1583">
                                  {item.tier}
                                </span>
                                <span className="text-muted-foreground" style={{
                          fontVariantNumeric: 'tabular-nums'
                        }} data-id="element-1584">
                                  {item.count} articoli ({item.pct}%)
                                </span>
                              </div>
                              <div className="h-2 bg-muted rounded-full overflow-hidden" data-id="element-1585">
                                <div className="h-full bg-accent rounded-full" style={{
                          width: `${item.pct}%`
                        }} data-id="element-1586" />
                              </div>
                            </div>)}
                        </div>
                      </section>}

                    {sections.sources && <section data-id="element-1587">
                        <h2 className="text-lg font-semibold mb-4 text-[#1A1F2E] flex items-center gap-2" data-id="element-1588">
                          <div className="w-1.5 h-1.5 rounded-full bg-accent" data-id="element-1589" />
                          Top Testate
                        </h2>
                        <div className="space-y-0 pl-4" data-id="element-1590">
                          {[{
                      name: 'Il Sole 24 Ore',
                      articles: 4,
                      score: 8.5
                    }, {
                      name: 'MF Milano Finanza',
                      articles: 3,
                      score: 8.1
                    }, {
                      name: 'Corriere della Sera',
                      articles: 2,
                      score: 7.8
                    }, {
                      name: 'La Repubblica',
                      articles: 2,
                      score: 7.2
                    }, {
                      name: 'Il Messaggero',
                      articles: 2,
                      score: 6.9
                    }].map(source => <div key={source.name} className="flex items-center justify-between py-2.5 border-b border-border last:border-0 text-sm" data-id="element-1591">
                              <span className="font-medium text-[#1A1F2E]" data-id="element-1592">
                                {source.name}
                              </span>
                              <div className="flex items-center gap-6 text-muted-foreground" data-id="element-1593">
                                <span style={{
                          fontVariantNumeric: 'tabular-nums'
                        }} data-id="element-1594">
                                  {source.articles} art.
                                </span>
                                <span className="font-semibold text-[#1A1F2E] w-8 text-right" style={{
                          fontVariantNumeric: 'tabular-nums'
                        }} data-id="element-1595">
                                  {source.score}
                                </span>
                              </div>
                            </div>)}
                        </div>
                      </section>}

                    {sections.spokespersons && <section data-id="element-1596">
                        <h2 className="text-lg font-semibold mb-4 text-[#1A1F2E] flex items-center gap-2" data-id="element-1597">
                          <div className="w-1.5 h-1.5 rounded-full bg-accent" data-id="element-1598" />
                          Portavoce citati
                        </h2>
                        <div className="grid grid-cols-2 gap-3 pl-4" data-id="element-1599">
                          {[{
                      name: 'Andrea Mangoni',
                      role: 'CEO Italia',
                      mentions: 8
                    }, {
                      name: 'Lars Eriksson',
                      role: 'Group CFO',
                      mentions: 4
                    }, {
                      name: 'Marco Bianchi',
                      role: 'Head of NPL',
                      mentions: 3
                    }, {
                      name: 'Sofia Rossi',
                      role: 'Head of Communications',
                      mentions: 2
                    }].map(person => <div key={person.name} className="p-3 border border-border rounded-lg flex items-center justify-between" data-id="element-1600">
                              <div data-id="element-1601">
                                <div className="text-sm font-medium text-[#1A1F2E]" data-id="element-1602">
                                  {person.name}
                                </div>
                                <div className="text-xs text-muted-foreground" data-id="element-1603">
                                  {person.role}
                                </div>
                              </div>
                              <div className="text-lg font-semibold text-accent" style={{
                        fontVariantNumeric: 'tabular-nums'
                      }} data-id="element-1604">
                                {person.mentions}
                              </div>
                            </div>)}
                        </div>
                      </section>}

                    {sections.allArticles && <section data-id="element-1605">
                        <h2 className="text-lg font-semibold mb-4 text-[#1A1F2E] flex items-center gap-2" data-id="element-1606">
                          <div className="w-1.5 h-1.5 rounded-full bg-accent" data-id="element-1607" />
                          Lista completa articoli
                        </h2>
                        <div className="border border-border rounded-lg overflow-hidden ml-4" data-id="element-1608">
                          <div className="bg-[#F8F9FA] px-4 py-2.5 flex gap-4 border-b border-border text-xs uppercase tracking-wider text-muted-foreground font-medium" data-id="element-1609">
                            <div className="w-20" data-id="element-1610">Data</div>
                            <div className="w-32" data-id="element-1611">Testata</div>
                            <div className="flex-1" data-id="element-1612">Titolo</div>
                            <div className="w-14 text-right" data-id="element-1613">Score</div>
                          </div>
                          {[{
                      date: '15/04',
                      source: 'Il Sole 24 Ore',
                      title: 'Intrum acquisisce portafoglio NPL da €1.2 miliardi',
                      score: '9.2'
                    }, {
                      date: '14/04',
                      source: 'MF Milano Finanza',
                      title: 'Credit management: Intrum rafforza la presenza',
                      score: '8.7'
                    }, {
                      date: '12/04',
                      source: 'Corriere della Sera',
                      title: 'Il mercato NPL italiano attira investitori',
                      score: '8.1'
                    }, {
                      date: '10/04',
                      source: 'La Repubblica',
                      title: 'Crediti deteriorati, il ruolo dei servicer nordeuropei',
                      score: '7.6'
                    }, {
                      date: '08/04',
                      source: 'Il Messaggero',
                      title: 'Intrum: piano di espansione nel Lazio',
                      score: '7.2'
                    }].map((row, i) => <div key={i} className="px-4 py-2.5 flex gap-4 border-b border-border last:border-0 text-sm" data-id="element-1614">
                              <div className="w-20 text-muted-foreground" style={{
                        fontVariantNumeric: 'tabular-nums'
                      }} data-id="element-1615">
                                {row.date}
                              </div>
                              <div className="w-32 text-muted-foreground truncate" data-id="element-1616">
                                {row.source}
                              </div>
                              <div className="flex-1 text-[#1A1F2E] font-medium truncate" data-id="element-1617">
                                {row.title}
                              </div>
                              <div className="w-14 text-right font-semibold" style={{
                        fontVariantNumeric: 'tabular-nums'
                      }} data-id="element-1618">
                                {row.score}
                              </div>
                            </div>)}
                        </div>
                      </section>}
                  </div>

                  {/* Footer */}
                  <div className="mt-16 pt-5 border-t border-border flex items-center justify-between" data-id="element-1619">
                    <div className="text-xs text-muted-foreground" data-id="element-1620">
                      UCEI — Media Intelligence
                    </div>
                    <div className="text-xs text-muted-foreground" data-id="element-1621">
                      Report generato automaticamente ·{' '}
                      {new Date().toLocaleDateString('it-IT')}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}